/**
 * TvLoginModal — Modal-based TV / QR Login Confirmation
 *
 * Flow:
 * 1. User enters a 6-char code displayed on TV
 * 2. On submit → calls loginWithCode → shows device info
 * 3. User confirms or cancels the login
 */
"use client";

import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalContent,
    ModalBody,
    Spinner,
} from "@nextui-org/react";
import { useTvLogin } from "@/hooks/useTvLogin";
import { enqueueSnackbar } from "notistack";
import "./TvLoginModal.css";

// ============================================================================
// Device name mapper (from TV user-agent to Persian label)
// ============================================================================

function getDeviceName(userAgent) {
    if (!userAgent) return "دستگاه ناشناس";
    const ua = userAgent.toLowerCase();
    if (ua.includes("tizen")) return "تلویزیون سامسونگ";
    if (ua.includes("web0s") || ua.includes("webos")) return "تلویزیون ال‌جی";
    if (ua.includes("android tv") || ua.includes("googletv")) return "اندروید تی‌وی";
    if (ua.includes("sony") || ua.includes("bravia")) return "تلویزیون سونی";
    if (ua.includes("xiaomi") || ua.includes("mitv")) return "تلویزیون شیائومی";
    if (ua.includes("smart-tv") || ua.includes("smarttv")) return "تلویزیون هوشمند";
    if (ua.includes("chrome") && !ua.includes("edg")) return "Chrome";
    if (ua.includes("edg")) return "Edge";
    if (ua.includes("firefox")) return "Firefox";
    if (ua.includes("safari") && !ua.includes("chrome")) return "Safari";
    return ua.length > 30 ? ua.slice(0, 30) + "…" : "دستگاه هوشمند";
}

// ============================================================================
// TvLoginModal
// ============================================================================

export default function TvLoginModal({ isOpen, onClose }) {
    const {
        status,
        deviceInfo,
        error,
        handleCodeSubmit,
        handleConfirm,
        handleCancel,
        reset,
    } = useTvLogin();

    const [code, setCode] = useState("");

    // Reset everything when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCode("");
            reset();
        }
    }, [isOpen, reset]);

    // ---- Submit code ----
    const onSubmitCode = () => {
        if (code.length < 6) return;
        handleCodeSubmit(code);
    };

    // ---- Confirm login ----
    const onConfirm = async () => {
        const result = await handleConfirm();
        if (result?.success) {
            enqueueSnackbar("ورود با موفقیت تأیید شد", {
                variant: "success",
            });
            onClose();
        }
    };

    // ---- Cancel login ----
    const onCancel = async () => {
        await handleCancel();
        // Goes back to idle (code input view)
    };

    const isLoading = status === "loading";
    const isConfirming = status === "confirming";

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            backdrop="blur"
            size="md"
            className="bg-[#1a1a1a]"
            classNames={{
                base: "!rounded-2xl border border-white/[0.08]",
            }}
            hideCloseButton={isLoading}
        >
            <ModalContent>
                <ModalBody className="!px-6 !py-8">
                    <div className="tv-login-modal">
                        {/* ---- Code Input View ---- */}
                        {!isConfirming && (
                            <>
                                <div className="tv-login-title">
                                    تأیید ورود با QR Code
                                </div>
                                <div className="tv-login-subtitle">
                                    کد نمایش داده شده روی تلویزیون یا دستگاه
                                    دیگر را وارد کنید
                                </div>

                                <div>
                                    <input
                                        type="text"
                                        maxLength={6}
                                        className="tv-login-input"
                                        value={code}
                                        onChange={(e) =>
                                            setCode(
                                                e.target.value
                                                    .replace(
                                                        /[^a-zA-Z0-9]/g,
                                                        ""
                                                    )
                                                    .toUpperCase()
                                            )
                                        }
                                        placeholder="ABC123"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" &&
                                                code.length >= 6
                                            ) {
                                                onSubmitCode();
                                            }
                                        }}
                                    />
                                </div>

                                <button
                                    className="tv-login-btn-primary"
                                    onClick={onSubmitCode}
                                    disabled={isLoading || code.length < 6}
                                >
                                    {isLoading ? (
                                        <span
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 8,
                                            }}
                                        >
                                            <Spinner
                                                size="sm"
                                                color="white"
                                            />
                                            در حال بررسی...
                                        </span>
                                    ) : (
                                        "ارسال کد"
                                    )}
                                </button>
                            </>
                        )}

                        {/* ---- Confirm Device View ---- */}
                        {isConfirming && deviceInfo && (
                            <>
                                <div className="tv-login-confirm-icon">📺</div>

                                <div className="tv-login-title">
                                    تأیید ورود دستگاه
                                </div>
                                <div className="tv-login-subtitle">
                                    آیا این درخواست ورود از طرف شماست؟
                                </div>

                                <div className="tv-login-info-card">
                                    {deviceInfo.userInfo?.fullName && (
                                        <div className="tv-login-info-row">
                                            <span className="tv-login-info-label">
                                                نام
                                            </span>
                                            <span className="tv-login-info-value">
                                                {deviceInfo.userInfo.fullName}
                                            </span>
                                        </div>
                                    )}
                                    <div className="tv-login-info-row">
                                        <span className="tv-login-info-label">
                                            دستگاه
                                        </span>
                                        <span className="tv-login-info-value">
                                            {getDeviceName(deviceInfo.device)}
                                        </span>
                                    </div>
                                    {deviceInfo.ip && (
                                        <div className="tv-login-info-row">
                                            <span className="tv-login-info-label">
                                                آدرس IP
                                            </span>
                                            <span className="tv-login-info-value">
                                                {deviceInfo.ip}
                                            </span>
                                        </div>
                                    )}
                                    {deviceInfo.userInfo?.mobileNumber && (
                                        <div className="tv-login-info-row">
                                            <span className="tv-login-info-label">
                                                شماره موبایل
                                            </span>
                                            <span className="tv-login-info-value">
                                                0
                                                {
                                                    deviceInfo.userInfo
                                                        .mobileNumber
                                                }
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <button
                                    className="tv-login-btn-primary"
                                    onClick={onConfirm}
                                    disabled={isLoading}
                                    style={{ marginTop: 4 }}
                                >
                                    {isLoading ? (
                                        <span
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 8,
                                            }}
                                        >
                                            <Spinner
                                                size="sm"
                                                color="white"
                                            />
                                            در حال تأیید...
                                        </span>
                                    ) : (
                                        "تأیید ورود"
                                    )}
                                </button>

                                <button
                                    className="tv-login-btn-secondary"
                                    onClick={onCancel}
                                    disabled={isLoading}
                                >
                                    رد درخواست
                                </button>
                            </>
                        )}

                        {/* ---- Error ---- */}
                        {error && (
                            <div className="tv-login-error">{error}</div>
                        )}
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
